// SolyticsListItem.jsx
// @ts-nocheck
/* eslint-disable react/prop-types */
/**
 * SolyticsListItem.jsx — INTERNAL COMPONENT
 *
 * Not intended for direct use outside SolyticsList.
 * All configuration is driven by the item object schema defined in SolyticsList.
 *
 * Rendering rules:
 * - item.onClick or item.href present → renders as ListItemButton (interactive)
 * - Neither present                   → renders as plain ListItem (static)
 * - item.custom present               → renders custom JSX directly inside ListItem
 */
import React from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SolyticsGrid from './SolyticsGrid';
import SolyticsCheckbox from './SolyticsCheckbox';
import SolyticsTooltip from './SolyticsTooltip';
import SolyticsBox from './SolyticsBox';
import { useDrag, useDrop } from 'react-dnd';

import { ReactComponent as DragIcon } from '../assets/DragIcon.svg';

const ItemTypes = {
  LIST_ITEM: 'LIST_ITEM',
};

// Default props as constants
const DEFAULT_DISABLE = false;
const DEFAULT_DIVIDER = false;
const DEFAULT_DENSE = false;
const DEFAULT_ALIGN_ITEMS = 'center';
const DEFAULT_AUTO_FOCUS = false;

// Applied to the <ListItem> wrapper.
// For interactive items, padding lives entirely on <ListItemButton> (see below),
// so we use disablePadding on the wrapper to avoid stacking two padding sources.
// For static items, the wrapper itself carries the padding.
const getListItemStyles =
  (dense, divider, alignItems, isInteractive, variant) => (theme) => ({
    alignItems,
    borderRadius: variant === 'flush' ? 0 : '4px',

    // Static items own their padding directly
    ...(!isInteractive && {
      padding: dense ? '4px 12px' : '8px 16px',
      minHeight: dense ? 36 : 44,
    }),

    ...(divider && {
      borderBottom: `1px solid ${theme.palette.divider}`,
    }),
    ...(isInteractive && {
      '&:hover .MuiListItemButton-root:not(.Mui-selected):not(.Mui-disabled)': {
        backgroundColor: theme.palette.action.hover,
      },
      '& .MuiListItemButton-root': {
        borderRadius: variant === 'flush' ? 0 : '4px',
      },
    }),

    // Focus visible styles for accessibility
    '&.Mui-focusVisible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: '-2px',
    },
    '&:focus': { outline: 'none' },
  });

// Applied to <ListItemButton> — owns all padding/height for interactive items.
const getButtonStyles = (dense) => (theme) => ({
  borderRadius: 0,
  // All vertical rhythm lives here, not on the ListItem wrapper
  padding: dense ? '4px 12px' : '8px 16px',
  minHeight: dense ? 32 : 44,
  transition: 'background-color 200ms cubic-bezier(0.4, 0, 0.2, 1)',

  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  '&.Mui-selected': {
    backgroundColor: theme.palette.action.selected,
    '&:hover': { backgroundColor: theme.palette.action.selected },
  },
  '&.Mui-disabled': {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'none',
  },
  '&.Mui-focusVisible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: '-2px',
    backgroundColor: theme.palette.action.focus,
  },
  '&:focus': { outline: 'none' },
});

const scrollableEllipsisSx = {
  whiteSpace: 'nowrap',
  overflowX: 'hidden',
  textOverflow: 'ellipsis',
};

/**
 * SolyticsListItem Component — INTERNAL COMPONENT
 *
 * Not intended for direct use outside SolyticsList.
 * All configuration is driven by the item object schema defined in SolyticsList.
 *
 * Rendering rules:
 *  - item.onClick or item.href present → renders as ListItemButton (interactive)
 *  - Neither present                   → renders as plain ListItem (static)
 *  - item.custom present               → renders custom JSX directly inside ListItem
 *
 * @param {Object} props - The component props.
 * @param {Object} [props.item={}] - The configuration object for the list item.
 * @param {string|React.ReactNode} [props.item.label] - Primary text of the item.
 * @param {string|React.ReactNode} [props.item.secondaryLabel] - Secondary/helper text.
 * @param {React.ReactNode} [props.item.icon] - Leading icon element.
 * @param {React.ReactNode} [props.item.endIcon] - Trailing icon element.
 * @param {React.ReactNode} [props.item.secondaryAction] - Far-right action slot element.
 * @param {React.ReactNode} [props.item.custom] - Escape hatch to render raw JSX inside ListItem.
 * @param {Function} [props.item.onClick] - Click handler, making the item interactive.
 * @param {string} [props.item.href] - URL for link, rendering as `<a>` and making the item interactive.
 * @param {boolean} [props.item.selected=false] - Whether the item is selected.
 * @param {boolean} [props.item.disabled=false] - Whether the item is disabled.
 * @param {boolean} [props.item.divider=false] - Whether to show a bottom divider.
 * @param {boolean} [props.item.dense=false] - Whether to use compact padding.
 * @param {string} [props.item.alignItems='center'] - The alignment of items.
 * @param {boolean} [props.item.autoFocus=false] - Whether the item focuses automatically.
 * @param {boolean} [props.item.checked] - If defined, renders a checkbox at the start matching this boolean value.
 * @param {Function} [props.item.onCheck] - Callback fired when the checkbox toggles. `(checked, event)`
 * @param {string} [props.item.id] - The unique identifier.
 * @param {string} [props.item.role] - ARIA role attribute.
 * @param {string} [props.item.ariaLabel] - ARIA label attribute.
 * @param {string} [props.item.ariaDescribedBy] - ARIA describedby attribute.
 * @param {number} [props.item.tabIndex] - Tab index attribute.
 * @returns {React.ReactElement} The rendered list item component.
 */
const BaseListItem = ({
  item = {},
  style,
  isDragging = false,
  isOver = false,
  dragRef,
  measureRef,
  dataIndex,
  draggable = false,
  handlerId,
}) => {
  const {
    label,
    secondaryLabel,
    icon,
    endIcon,
    secondaryAction,
    custom,
    onClick,
    href,
    selected = false,
    disabled = DEFAULT_DISABLE,
    divider = DEFAULT_DIVIDER,
    dense = DEFAULT_DENSE,
    alignItems = DEFAULT_ALIGN_ITEMS,
    autoFocus = DEFAULT_AUTO_FOCUS,
    checked,
    indeterminate,
    id,
    role,
    ariaLabel,
    ariaDescribedBy,
    tabIndex,
    variant,
  } = item;

  // Infer interactive mode — no explicit `button` prop needed
  // Note: if there is an onCheck without onClick, we still might want button styling
  // for full row clickability, or let the user click specifically the checkbox.
  // We'll respect onClick or href explicitly.
  const isInteractive = Boolean(onClick);

  const listItemProps = {
    id,
    ref: (node) => {
      if (dragRef) dragRef(node);
      if (measureRef) measureRef(node);
    },
    'data-index': dataIndex,
    'data-handler-id': handlerId,
    disabled,
    disablePadding: isInteractive,
    divider,
    dense,
    alignItems,
    tabIndex: isInteractive ? undefined : tabIndex,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
    'aria-disabled': disabled ? 'true' : undefined,
    style,
    sx: [
      getListItemStyles(dense, divider, alignItems, isInteractive, variant),
      !isInteractive &&
        selected && {
          backgroundColor: (theme) => theme.palette.action.selected,
          '&:hover': {
            backgroundColor: (theme) => theme.palette.action.selected,
          },
        },
      {
        // 1. Drag & Drop Base Styling (Opacity & Position)
        opacity: isDragging ? 0.5 : 1,
        position: 'relative',
        transition: 'background-color 0.2s ease',

        // 2. Cursor Physics mimicking the table column ordering
        cursor: disabled
          ? 'default'
          : isDragging
            ? 'grabbing'
            : draggable
              ? 'grab'
              : 'default',
      },
      // 3. Hover state for draggable rows
      draggable &&
        !disabled && {
          '&:hover': {
            backgroundColor: (theme) =>
              selected
                ? theme.palette.action.selected
                : theme.palette.grey?.[30],
          },
        },
    ],
    ...(secondaryAction && { secondaryAction }),
  };

  const innerContent = custom ?? (
    <>
      {/* 4. Drag Handle Icon */}
      {draggable && (
        <ListItemIcon
          sx={{
            minWidth: 32,
            display: 'flex',
            alignItems: 'center',
            opacity: disabled ? 0.4 : 1,
          }}
        >
          <DragIcon
            style={{ width: '18px', height: '18px', color: '#727272' }}
          />
        </ListItemIcon>
      )}

      {checked !== undefined && (
        <ListItemIcon sx={{ minWidth: 36 }}>
          <SolyticsCheckbox
            id={id ? `${id}-checkbox` : undefined}
            name={id ? `${id}-checkbox` : 'list-item-checkbox'}
            checked={checked}
            indeterminate={indeterminate}
            disabled={disabled}
          />
        </ListItemIcon>
      )}
      {icon && <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>}
      <SolyticsTooltip text={label}>
        <SolyticsGrid sx={{ display: 'block', overflow: 'hidden' }}>
          <ListItemText
            primary={label}
            secondary={secondaryLabel ?? undefined}
            sx={{ minWidth: 0 }}
            slotProps={{
              primary: {
                variant: 'body2',
                sx: scrollableEllipsisSx,
              },
              secondary: {
                variant: 'caption',
                sx: scrollableEllipsisSx,
              },
            }}
          />
        </SolyticsGrid>
      </SolyticsTooltip>

      {endIcon && (
        <ListItemIcon
          sx={{ minWidth: 'unset', ml: 1, justifyContent: 'flex-end' }}
        >
          {endIcon}
        </ListItemIcon>
      )}
    </>
  );

  // 5. Absolute positioning Drop Indicator (matches Table Column Ordering)
  const dropIndicator = isOver && (
    <SolyticsBox
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        height: 2,
        backgroundColor: 'secondary.main',
        zIndex: 2,
      }}
    />
  );

  // ── Interactive (button) variant ───────────────────────────────────────────
  if (isInteractive) {
    return (
      <ListItem {...listItemProps}>
        <ListItemButton
          disabled={disabled}
          selected={selected}
          autoFocus={autoFocus}
          onClick={(e) => {
            // If the user completely taps the row, we might want to also toggle the checkbox
            // depending on standard behavior. Often, developers handle `onCheck` separately
            // but we provide standard onClick behavior here.
            onClick(e);
          }}
          href={href}
          component={href ? 'a' : 'div'}
          role={role ?? 'button'}
          tabIndex={disabled ? -1 : (tabIndex ?? 0)}
          aria-selected={selected ? 'true' : 'false'}
          aria-describedby={ariaDescribedBy}
          sx={{
            ...getButtonStyles(dense),
            ...(draggable && { cursor: 'inherit' }),
          }}
        >
          {innerContent}
        </ListItemButton>
        {dropIndicator}
      </ListItem>
    );
  }

  // Render simple list item
  return (
    <ListItem role={role} {...listItemProps}>
      {innerContent}
      {dropIndicator}
    </ListItem>
  );
};

const DraggableListItemWrapper = (props) => {
  const { item, index, draggable, onDrop: onDropProp } = props;
  const disabled = item?.disabled || DEFAULT_DISABLE;
  const id = item?.id;

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.LIST_ITEM,
    item: () => ({ id, index }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    canDrag: draggable && !disabled,
  });

  const [{ isOver, handlerId }, drop] = useDrop({
    accept: ItemTypes.LIST_ITEM,
    canDrop: (draggedItem) => !disabled && draggedItem.index !== index,
    drop: (draggedItem, monitor) => {
      // Prevents dropping if handled by a nested drop target
      if (!monitor.getDropResult()) {
        onDropProp?.(draggedItem.index, index);
      }
    },
    collect: (monitor) => ({
      handlerId: monitor.getHandlerId(),
      isOver: !!monitor.isOver({ shallow: true }),
    }),
  });

  const attachRef = (node) => {
    if (draggable && !disabled) {
      drag(drop(node));
    }
  };

  return (
    <BaseListItem
      {...props}
      isDragging={isDragging}
      isOver={isOver}
      dragRef={attachRef}
      handlerId={handlerId}
      draggable={draggable}
    />
  );
};

const SolyticsListItem = (props) => {
  if (props.draggable) {
    return <DraggableListItemWrapper {...props} />;
  }
  return <BaseListItem {...props} />;
};

export default SolyticsListItem;
